import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API_BASE, { withApiCredentials } from "../../api";
import { trackProductEvent } from "../../analytics/trackEvent";
import {
  buildTypeOfFishingFilterOptions,
  getTypeOfFishingOptionsForWay,
} from "../../utils/catchMetadataOptions";
import {
  getGroupLocationSuggestionCoordinates,
  getGroupLocationSuggestionLabel,
  readErrorDetail,
} from "./GroupsSharedUi";

export default function useGroupCreateFormState({
  fishingMethods,
  isCreateOpen = false,
  token,
  user,
  t,
  onCreateSuccess,
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [groupRules, setGroupRules] = useState("");
  const [groupType, setGroupType] = useState("");
  const [groupWaterType, setGroupWaterType] = useState("all");
  const [groupWayOfFishing, setGroupWayOfFishing] = useState("all");
  const [groupTypeOfFishing, setGroupTypeOfFishing] = useState("all");
  const [groupLocation, setGroupLocation] = useState("");
  const [groupLocationSuggestions, setGroupLocationSuggestions] = useState([]);
  const [selectedGroupLocationCoordinates, setSelectedGroupLocationCoordinates] =
    useState(null);
  const [groupCoverFile, setGroupCoverFile] = useState(null);
  const [groupCoverPreviewUrl, setGroupCoverPreviewUrl] = useState("");
  const [groupCoverPosition, setGroupCoverPosition] = useState({
    x: 50,
    y: 50,
    scale: 1,
  });
  const [visibility, setVisibility] = useState("public");
  const [postingApprovalMode, setPostingApprovalMode] = useState(
    "member_posts_auto_publish"
  );
  const [isVisibilityHelpOpen, setIsVisibilityHelpOpen] = useState(false);
  const [isGroupTypeHelpOpen, setIsGroupTypeHelpOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const visibilityHelpRef = useRef(null);
  const groupTypeHelpRef = useRef(null);
  const groupLocationFieldRef = useRef(null);
  const groupCoverInputRef = useRef(null);

  const groupTypeOfFishingOptions = useMemo(() => {
    if (groupWayOfFishing === "all") {
      return buildTypeOfFishingFilterOptions(fishingMethods).map(
        (option) => option.label
      );
    }

    return getTypeOfFishingOptionsForWay(fishingMethods, groupWayOfFishing);
  }, [fishingMethods, groupWayOfFishing]);

  const clearGroupCover = useCallback(() => {
    setGroupCoverFile(null);
    setGroupCoverPosition({ x: 50, y: 50, scale: 1 });
    if (groupCoverInputRef.current) {
      groupCoverInputRef.current.value = "";
    }
  }, []);

  const openCreateForm = useCallback(() => {
    setCreateError("");
  }, []);

  const closeCreateForm = useCallback(() => {
    setCreateError("");
  }, []);

  const cancelCreateGroup = useCallback(() => {
    setCreateError("");
    setGroupRules("");
    setGroupType("");
    setGroupWaterType("all");
    setGroupWayOfFishing("all");
    setGroupTypeOfFishing("all");
    setGroupLocation("");
    setGroupLocationSuggestions([]);
    setSelectedGroupLocationCoordinates(null);
    setVisibility("public");
    setPostingApprovalMode("member_posts_auto_publish");
    setIsGroupTypeHelpOpen(false);
    setIsVisibilityHelpOpen(false);
    clearGroupCover();
  }, [clearGroupCover]);

  const toggleGroupTypeHelp = useCallback(() => {
    setIsVisibilityHelpOpen(false);
    setIsGroupTypeHelpOpen((prev) => !prev);
  }, []);

  const toggleVisibilityHelp = useCallback(() => {
    setIsGroupTypeHelpOpen(false);
    setIsVisibilityHelpOpen((prev) => !prev);
  }, []);

  const selectGroupLocationSuggestion = useCallback((option) => {
    const optionLabel = getGroupLocationSuggestionLabel(option);
    const optionCoordinates = getGroupLocationSuggestionCoordinates(option);
    setGroupLocation(optionLabel);
    setSelectedGroupLocationCoordinates(optionCoordinates);
    setGroupLocationSuggestions([]);
    setCreateError("");
  }, []);

  function handleGroupTypeChange(nextGroupType) {
    setGroupType(nextGroupType);
    setCreateError("");

    if (nextGroupType !== "local") {
      setGroupLocation("");
      setGroupLocationSuggestions([]);
      setSelectedGroupLocationCoordinates(null);
    }
  }

  function handleGroupWayOfFishingChange(nextWayOfFishing) {
    setGroupWayOfFishing(nextWayOfFishing);
    setGroupTypeOfFishing("all");
  }

  const handleGroupCoverChange = useCallback((event) => {
    const nextFile = event.target.files?.[0] || null;
    setGroupCoverFile(nextFile);
    setGroupCoverPosition({ x: 50, y: 50, scale: 1 });
  }, []);

  async function handleGroupLocationChange(value) {
    setGroupLocation(value);
    setSelectedGroupLocationCoordinates(null);
    setCreateError("");

    if (!value || value.trim().length < 2) {
      setGroupLocationSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/geo/places?q=${encodeURIComponent(value.trim())}`
      );
      const data = await response.json().catch(() => []);
      setGroupLocationSuggestions(Array.isArray(data) ? data : []);
    } catch {
      setGroupLocationSuggestions([]);
    }
  }

  useEffect(() => {
    if (!isCreateOpen) {
      setIsVisibilityHelpOpen(false);
      setIsGroupTypeHelpOpen(false);
      setGroupLocationSuggestions([]);
    }
  }, [isCreateOpen]);

  useEffect(() => {
    if (!isVisibilityHelpOpen) return;

    function handleVisibilityHelpPointerDown(event) {
      if (!visibilityHelpRef.current?.contains(event.target)) {
        setIsVisibilityHelpOpen(false);
      }
    }

    function handleVisibilityHelpKeyDown(event) {
      if (event.key === "Escape") {
        setIsVisibilityHelpOpen(false);
      }
    }

    document.addEventListener("mousedown", handleVisibilityHelpPointerDown);
    document.addEventListener("keydown", handleVisibilityHelpKeyDown);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleVisibilityHelpPointerDown
      );
      document.removeEventListener("keydown", handleVisibilityHelpKeyDown);
    };
  }, [isVisibilityHelpOpen]);

  useEffect(() => {
    if (!isGroupTypeHelpOpen) return;

    function handleGroupTypeHelpPointerDown(event) {
      if (!groupTypeHelpRef.current?.contains(event.target)) {
        setIsGroupTypeHelpOpen(false);
      }
    }

    function handleGroupTypeHelpKeyDown(event) {
      if (event.key === "Escape") {
        setIsGroupTypeHelpOpen(false);
      }
    }

    document.addEventListener("mousedown", handleGroupTypeHelpPointerDown);
    document.addEventListener("keydown", handleGroupTypeHelpKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleGroupTypeHelpPointerDown);
      document.removeEventListener("keydown", handleGroupTypeHelpKeyDown);
    };
  }, [isGroupTypeHelpOpen]);

  useEffect(() => {
    if (!groupLocationSuggestions.length) return;

    function handleGroupLocationPointerDown(event) {
      if (!groupLocationFieldRef.current?.contains(event.target)) {
        setGroupLocationSuggestions([]);
      }
    }

    function handleGroupLocationKeyDown(event) {
      if (event.key === "Escape") {
        setGroupLocationSuggestions([]);
      }
    }

    document.addEventListener("mousedown", handleGroupLocationPointerDown);
    document.addEventListener("keydown", handleGroupLocationKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleGroupLocationPointerDown);
      document.removeEventListener("keydown", handleGroupLocationKeyDown);
    };
  }, [groupLocationSuggestions.length]);

  useEffect(() => {
    if (!groupCoverFile) {
      setGroupCoverPreviewUrl("");
      return;
    }

    const nextPreviewUrl = URL.createObjectURL(groupCoverFile);
    setGroupCoverPreviewUrl(nextPreviewUrl);

    return () => {
      URL.revokeObjectURL(nextPreviewUrl);
    };
  }, [groupCoverFile]);

  async function handleCreateGroup(event) {
    event.preventDefault();

    if (!user) return;

    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    const trimmedGroupRules = groupRules.trim();
    const trimmedGroupLocation = groupLocation.trim();

    if (!trimmedName) {
      setCreateError(t("groups_name_required"));
      return;
    }

    if (!groupType) {
      setCreateError(t("groups_type_required"));
      return;
    }

    if (groupType === "local" && !trimmedGroupLocation) {
      setCreateError(t("groups_location_required"));
      return;
    }

    setIsCreating(true);
    setCreateError("");

    try {
      const formData = new FormData();
      formData.append("name", trimmedName);
      formData.append("description", trimmedDescription);
      formData.append("groupRules", trimmedGroupRules);
      formData.append("groupType", groupType);
      formData.append("visibility", visibility);
      formData.append("postingApprovalMode", postingApprovalMode);
      formData.append("waterType", groupWaterType);
      formData.append("wayOfFishing", groupWayOfFishing);
      formData.append("typeOfFishing", groupTypeOfFishing);

      if (groupType === "local") {
        formData.append("locationName", trimmedGroupLocation);

        if (selectedGroupLocationCoordinates) {
          formData.append(
            "locationLatitude",
            selectedGroupLocationCoordinates.lat.toString()
          );
          formData.append(
            "locationLongitude",
            selectedGroupLocationCoordinates.lng.toString()
          );
        }
      }

      if (groupCoverFile) {
        formData.append("coverImage", groupCoverFile);
        formData.append("coverPositionX", groupCoverPosition.x.toString());
        formData.append("coverPositionY", groupCoverPosition.y.toString());
        formData.append("coverScale", groupCoverPosition.scale.toString());
      }

      const response = await fetch(
        `${API_BASE}/groups/`,
        withApiCredentials({
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        })
      );

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(readErrorDetail(data, t("groups_create_failed")));
      }

      setName("");
      setDescription("");
      setGroupRules("");
      setGroupType("");
      setGroupWaterType("all");
      setGroupWayOfFishing("all");
      setGroupTypeOfFishing("all");
      setGroupLocation("");
      setGroupLocationSuggestions([]);
      setSelectedGroupLocationCoordinates(null);
      clearGroupCover();
      setVisibility("public");
      setPostingApprovalMode("member_posts_auto_publish");
      setIsGroupTypeHelpOpen(false);
      setIsVisibilityHelpOpen(false);
      trackProductEvent("group_created", {
        featureArea: "groups",
        metadata: {
          source: "group_create_form",
          group_type: groupType,
          visibility,
          posting_approval_mode: postingApprovalMode,
          has_location: groupType === "local",
          has_cover: Boolean(groupCoverFile),
        },
      });
      onCreateSuccess?.(data);
      window.dispatchEvent(new Event("groupsUpdated"));
    } catch (err) {
      setCreateError(err.message || t("groups_create_failed"));
    } finally {
      setIsCreating(false);
    }
  }

  return {
    isCreateOpen,
    name,
    setName,
    description,
    setDescription,
    groupRules,
    setGroupRules,
    groupType,
    groupWaterType,
    setGroupWaterType,
    groupWayOfFishing,
    groupTypeOfFishing,
    setGroupTypeOfFishing,
    groupLocation,
    groupLocationSuggestions,
    groupCoverFile,
    groupCoverPreviewUrl,
    groupCoverPosition,
    setGroupCoverPosition,
    visibility,
    setVisibility,
    postingApprovalMode,
    setPostingApprovalMode,
    isVisibilityHelpOpen,
    isGroupTypeHelpOpen,
    isCreating,
    createError,
    groupCoverInputRef,
    groupTypeHelpRef,
    visibilityHelpRef,
    groupLocationFieldRef,
    groupTypeOfFishingOptions,
    openCreateForm,
    closeCreateForm,
    cancelCreateGroup,
    toggleGroupTypeHelp,
    toggleVisibilityHelp,
    selectGroupLocationSuggestion,
    handleGroupTypeChange,
    handleGroupWayOfFishingChange,
    handleGroupCoverChange,
    clearGroupCover,
    handleGroupLocationChange,
    handleCreateGroup,
  };
}
